import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card } from "../components/ui/card";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { Calculator, Building2, Ruler, Home } from "lucide-react";

export default function FARCalculator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ size_acres: 1, road_width_ft: 40, zone: "Residential", project_type: "Residential" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Please sign in to use the calculator"); navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/calc/far", form);
      setResult(data);
    } catch { toast.error("Calculation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Interactive Tools</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">JD & FAR Calculator</h1>
      <p className="mt-2 text-slate-400 text-sm max-w-2xl">Estimate permissible FAR, built-up area, and recommended Joint Development revenue splits based on Bangalore byelaw approximations.</p>

      <div className="mt-12 grid lg:grid-cols-2 gap-8">
        <Card className="bg-[#1E293B] border-[#334155] p-8">
          <form onSubmit={submit} className="space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Parcel Size (Acres)</Label>
              <Input data-testid={TID.calcSize} type="number" step="0.01" min="0.1" required
                value={form.size_acres}
                onChange={(e) => setForm({ ...form, size_acres: parseFloat(e.target.value) })}
                className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Road Width (ft)</Label>
              <Input data-testid={TID.calcRoad} type="number" min="10" required
                value={form.road_width_ft}
                onChange={(e) => setForm({ ...form, road_width_ft: parseFloat(e.target.value) })}
                className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Master Plan Zone</Label>
              <Select value={form.zone} onValueChange={(v) => setForm({ ...form, zone: v })}>
                <SelectTrigger data-testid={TID.calcZone} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                  {["Residential", "Commercial", "Mixed Use", "Industrial"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Project Type</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                <SelectTrigger data-testid={TID.calcProjectType} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                  {["Residential", "Commercial"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid={TID.calcSubmit} type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
              <Calculator className="h-4 w-4 mr-2" /> {busy ? "Calculating…" : "Calculate FAR & Split"}
            </Button>
          </form>
        </Card>

        <div data-testid={TID.calcResult}>
          {result ? (
            <Card className="bg-[#1E293B] border-[#334155] p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Result</div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <Stat icon={Ruler} label="Permissible FAR" value={result.far} />
                <Stat icon={Building2} label="Plot Area (sqft)" value={result.plot_sqft.toLocaleString()} />
                <Stat icon={Building2} label="Built-up (sqft)" value={result.buildup_sqft.toLocaleString()} accent />
                <Stat icon={Home} label="Saleable (sqft)" value={result.saleable_sqft.toLocaleString()} />
                <Stat icon={Ruler} label="Max Height (m)" value={result.max_height_m} />
                <Stat icon={Building2} label="Recommended Split" value={result.recommended_split} />
              </div>
              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                Estimates are indicative and follow BBMP/BDA byelaw approximations. Always validate with our in-house advocates before contracting.
              </p>
            </Card>
          ) : (
            <Card className="bg-[#1E293B] border-[#334155] p-8 h-full grid place-items-center text-slate-500 text-sm">
              Enter parcel details to see FAR estimates.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-[#334155] bg-[#0F172A] p-4">
      <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-[0.2em]">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`mt-2 font-display text-xl font-semibold ${accent ? "text-[#10B981]" : "text-white"}`}>{value}</div>
    </div>
  );
}
